// @flow
import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {makeStyles} from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import SnackBar from '../../../../elements/SnackBar/SnackBar';
import Collapsible from '../../../../elements/Collapsible/Collapsible';
import {FileCopyOutlined} from '@material-ui/icons';
import type {Store} from '../../../../entities';
import {
  Button,
  ClickAwayListener,
  Divider,
  Tooltip,
  Link,
} from '@material-ui/core';
import {colors} from '../../../../assets/styles/colors';
import {endpoints} from '../../../products/api';
import {getBasicDomain, useIsFeatureInTier} from '../../../../utils/component';
import TierRestrictionModal from '../../../../elements/TierRestrictionModal/TierRestrictionModal';
import {useHistory} from 'react-router-dom';
import CustomTextField from '../../../../elements/CustomTextField/CustomTextField';
import CustomButton from '../../../../elements/CustomButton/CustomButton';
import {useForm} from 'react-hook-form';
import type {
  FacebookIntegration as FacebookIntegrationType,
  UiAction,
} from '../../../ui/duck';
import {useCallback} from 'react';
import {Alert, AlertTitle} from '@material-ui/lab';
import {useStoreCountry} from '../../../country/utils';

type Props = {|
  store?: Store,
  fetchFbFeedToken: () => void,
  fbFeedToken?: string,
  fetchFbFeedTokenError?: Boolean,
  facebookIntegration?: FacebookIntegrationType,
  saveFacebookIntegration: (payload: UiAction) => void,
  fetchFacebookIntegration: () => void,
  isFetchingFacebookIntegration?: boolean,
  saveFacebookIntegrationSuccess?: boolean,
|};

const useStyles = makeStyles(theme => ({
  subTitle: {
    marginBottom: theme.spacing(2),
    fontSize: '0.875rem',
    color: colors.grey700,
  },
  marginTopNone: {
    marginTop: '0 !important',
  },
  saveButton: {
    marginTop: theme.spacing(2),
  },
  logoImg: {
    width: 20,
    height: 20,
    opacity: 0.6,
    marginTop: '14px',
    marginRight: theme.spacing(2),
  },
  facebookButton: {
    marginTop: theme.spacing(3),
    width: '100%',
    height: 56,
    background: 'linear-gradient(180deg, #1778F2 0%, #0A6CE7 100%)',
    borderRadius: '12px',
    color: 'white',
    justifyContent: 'flex-start',
  },
  copyFbLinkBtn: {
    textTransform: 'none',
    borderRadius: '8px',
    maxWidth: '100%',
    width: 400,
    whiteSpace: 'nowrap',
  },
  copyFbLinkBtnLabel: {
    display: 'block',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  divider: {
    margin: theme.spacing(5, 0),
    background: colors.grey200,
    width: '100%',
  },
  sectionTitle: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  sectionTitleFont: {
    fontWeight: 600,
  },
  connectionState: {
    marginRight: theme.spacing(1),
    fontSize: '0.875rem',
    color: colors.grey700,
  },
  connected: {
    color: colors.success,
  },
}));

export const igFbShopAvailableCountries = [
  'CA',
  'US',
  'BR',
  'MX',
  'DK',
  'FR',
  'DE',
  'IT',
  'NL',
  'NO',
  'ES',
  'SE',
  'CH',
  'UA',
  'GB',
  'AU',
  'IN',
  'ID',
  'JP',
  'KR',
  'TW',
  'TH',
];

export default function FacebookIntegration(props: Props) {
  const {
    store,
    fetchFbFeedToken,
    fbFeedToken,
    fetchFbFeedTokenError,
    facebookIntegration,
    saveFacebookIntegration,
    fetchFacebookIntegration,
    isFetchingFacebookIntegration,
    saveFacebookIntegrationSuccess,
  } = props;
  const classes = useStyles();
  const history = useHistory();

  const country = useStoreCountry();

  const [linkCopied, setLinkCopied] = useState(false);

  const [tierRestrictionModal, setTierRestrictionModal] = useState(false);

  const hasFbFeature = useIsFeatureInTier('FB_STORE');

  useEffect(() => {
    if (store) {
      if (hasFbFeature) {
        fetchFbFeedToken();
        fetchFacebookIntegration();
      } else {
        setTierRestrictionModal(true);
      }
    }
  }, [fetchFacebookIntegration, fetchFbFeedToken, hasFbFeature, store]);

  const {handleSubmit, register, setValue, errors} = useForm();

  const [fBMTParsed, setFBMTParsed] = useState(false);
  const [pIParsed, setPIParsed] = useState(false);
  const [save, setSave] = useState(false);

  const processMetaTag = (string: string) => {
    // console.log('processing ', string);
    if (string) {
      let match = /<meta.*name="facebook-domain-verification".*content="(.*)".*\/>/.exec(
        string
      );
      if (!match) match = /facebook-domain-verification=(.*)/.exec(string);
      if (match && match[1]) {
        setValue('fbMetaTag', match[1]);
      }
    }
    return true;
  };
  const handleMetaTag = async e => {
    setFBMTParsed(false);
    // if (e.target.value) {
    const done = await processMetaTag(e.target.value);
    setFBMTParsed(done);
    // }
  };

  const processPixelId = (string: string) => {
    if (string) {
      let match = /fbq\('init',[ ]??'(\d+?)'\);/.exec(string);
      if (match && match[1]) {
        setValue('pixelId', match[1]);
      }
    }
    return true;
  };
  const handlePixelId = async e => {
    setPIParsed(false);
    // if (e.target.value) {
    const done = await processPixelId(e.target.value);
    setPIParsed(done);
    // }
  };

  const onSubmit = useCallback(
    (data, field: string) => {
      switch (field) {
        case 'fbMetaTag':
          if (
            (data[field] &&
              (!facebookIntegration || !facebookIntegration.validationToken)) ||
            (facebookIntegration &&
              data[field] !== facebookIntegration.validationToken)
          ) {
            saveFacebookIntegration({
              saveFacebookIntegrationRequest: {
                validationToken: data[field].trim(),
              },
            });
            setSave(false);
          }
          break;
        case 'pixelId':
          if (
            (data[field] &&
              (!facebookIntegration || !facebookIntegration.pixelId)) ||
            (facebookIntegration && data[field] !== facebookIntegration.pixelId)
          ) {
            saveFacebookIntegration({
              saveFacebookIntegrationRequest: {pixelId: data[field].trim()},
            });
            setSave(false);
          }
          break;
        default:
          break;
      }
    },
    [facebookIntegration, saveFacebookIntegration]
  );

  useEffect(() => {
    if (fBMTParsed && save) {
      handleSubmit(onSubmit)('fbMetaTag');
    }
  }, [fBMTParsed, handleSubmit, onSubmit, save]);

  useEffect(() => {
    if (pIParsed && save) {
      handleSubmit(onSubmit)('pixelId');
    }
  }, [handleSubmit, onSubmit, pIParsed, save]);

  const igFbShopAvailable = igFbShopAvailableCountries.includes(country?.code);

  return (
    <>
      {/* <CustomExitPrompt when={isDirty} /> */}
      <SnackBar
        mode="error"
        message={
          fetchFbFeedTokenError ? 'Error al generar link de integración' : ''
        }
      />
      <SnackBar
        mode="success"
        message={saveFacebookIntegrationSuccess ? 'Configuración guardada' : ''}
      />
      <br />
      {!igFbShopAvailable && (
        <Alert severity="warning">
          <AlertTitle>Atención</AlertTitle>
          La integración con Facebook Shop e Instagram Shopping no está
          disponible actualmente en tu país. Para más información, visita el{' '}
          <Link
            href="https://www.facebook.com/business/help/549256849084694/"
            target="_blank"
            rel="noopener noreferrer"
          >
            Centro de Ayuda de Meta.
          </Link>
        </Alert>
      )}
      <br />
      {igFbShopAvailable && (
        <Collapsible
          title="Facebook Shop / Instagram Shopping"
          className={classes.marginTopNone}
        >
          <Typography variant="body1" className={classes.subTitle}>
            Utiliza el siguiente link para sincronizar tu catálogo tiendada con
            Facebook Shop / Intagram Shopping.
          </Typography>
          <Typography variant="body1" className={classes.subTitle}>
            Copia el siguiente link:{' '}
          </Typography>
          {!!fbFeedToken && !!store && (
            <>
              <ClickAwayListener onClickAway={() => setLinkCopied(false)}>
                <Tooltip
                  placement="right"
                  arrow
                  PopperProps={{
                    disablePortal: true,
                  }}
                  onClose={() => setLinkCopied(false)}
                  open={linkCopied}
                  disableFocusListener
                  disableHoverListener
                  disableTouchListener
                  leaveDelay={300}
                  title="¡Link copiado!"
                >
                  <Button
                    variant="contained"
                    color="primary"
                    disableElevation
                    className={classes.copyFbLinkBtn}
                    endIcon={<FileCopyOutlined />}
                    onClick={() => {
                      navigator &&
                        navigator.clipboard &&
                        navigator.clipboard
                          .writeText(
                            `https://${getBasicDomain(store.domains)}${
                              endpoints.FB_FEED_LINK
                            }?store_id=${
                              store.id
                            }&format=csv&token=${fbFeedToken}`
                          )
                          .then(() => setLinkCopied(true))
                          .catch(e => console.error(e));
                    }}
                  >
                    <span
                      className={classes.copyFbLinkBtnLabel}
                    >{`https://${getBasicDomain(store.domains)}${
                      endpoints.FB_FEED_LINK
                    }?store_id=${
                      store.id
                    }&format=csv&token=${fbFeedToken}`}</span>
                  </Button>
                </Tooltip>
              </ClickAwayListener>
            </>
          )}
          <Divider className={classes.divider} />
          <a
            href="https://tiendadateam.notion.site/Integraci-n-con-Facebook-e-Instagram-Shop-y-Verificaci-n-de-dominio-en-Facebook-6383291a040e4f859fd7e043a94dd117"
            target="_blank"
            rel="noopener noreferrer"
          >
            <p>
              Seguir los siguientes pasos <b>click aquí.</b>
            </p>
          </a>
          <a
            href="https://business.facebook.com/commerce"
            target="_blank"
            rel="noopener noreferrer"
          >
            <p>Ingresa a tu cuenta de comercio en facebook.</p>
          </a>
        </Collapsible>
      )}
      <Collapsible
        title={
          <div className={classes.sectionTitle}>
            <Typography className={classes.sectionTitleFont}>
              Verificación de dominio
            </Typography>
            <Typography
              className={`${classes.connectionState} ${
                !!facebookIntegration && !!facebookIntegration.validationToken
                  ? classes.connected
                  : ''
              }`}
            >
              Estado de conexión:{' '}
              {facebookIntegration && facebookIntegration.validationToken
                ? 'Conectado'
                : 'Desconectado'}
            </Typography>
          </div>
        }
      >
        {!isFetchingFacebookIntegration && (
          <CustomTextField
            label="Pegar meta tag de Facebook"
            name="fbMetaTag"
            error={!!errors['fbMetaTag']}
            helperText={errors['fbMetaTag'] ? errors['fbMetaTag'].message : ''}
            inputRef={register()}
            defaultValue={
              facebookIntegration ? facebookIntegration.validationToken : ''
            }
            type="text"
            onBlur={handleMetaTag}
            disabled={!hasFbFeature}
          />
        )}
        <CustomButton
          className={classes.saveButton}
          variant="contained"
          onClick={() => setSave(true)}
          disabled={!hasFbFeature}
        >
          Guardar Cambios
        </CustomButton>
      </Collapsible>
      <Collapsible
        title={
          <div className={classes.sectionTitle}>
            <Typography className={classes.sectionTitleFont}>
              Facebook Pixel
            </Typography>
            <Typography
              className={`${classes.connectionState} ${
                !!facebookIntegration && !!facebookIntegration.pixelId
                  ? classes.connected
                  : ''
              }`}
            >
              Estado de conexión:{' '}
              {facebookIntegration && facebookIntegration.pixelId
                ? 'Conectado'
                : 'Desconectado'}
            </Typography>
          </div>
        }
      >
        {!isFetchingFacebookIntegration && (
          <CustomTextField
            label="Pegar ID de Pixel"
            name="pixelId"
            error={!!errors['pixelId']}
            helperText={errors['pixelId'] ? errors['pixelId'].message : ''}
            inputRef={register()}
            defaultValue={
              facebookIntegration ? facebookIntegration.pixelId : ''
            }
            type="text"
            onBlur={handlePixelId}
            disabled={!hasFbFeature}
          />
        )}
        <CustomButton
          className={classes.saveButton}
          variant="contained"
          disabled={!hasFbFeature}
          // onClick={() => handleSubmit(onSubmit)('pixelId')}
          onClick={() => setSave(true)}
        >
          Guardar Cambios
        </CustomButton>
      </Collapsible>
      <TierRestrictionModal
        open={tierRestrictionModal}
        handleClose={() => history.replace('/my-store/social-medias')}
      />
    </>
  );
}

FacebookIntegration.propTypes = {
  store: PropTypes.object,
  fetchFbFeedToken: PropTypes.func.isRequired,
  fbFeedToken: PropTypes.string,
  fetchFbFeedTokenError: PropTypes.bool,
  facebookIntegration: PropTypes.object,
  saveFacebookIntegration: PropTypes.func.isRequired,
  fetchFacebookIntegration: PropTypes.func.isRequired,
  isFetchingFacebookIntegration: PropTypes.bool,
  saveFacebookIntegrationSuccess: PropTypes.bool,
};
